import { useState } from "react";

import { AsyncPaginate } from "react-select-async-paginate";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useCustomerViewModel } from "../../../viewmodels/operation/customer/useCustomerViewModel";

import { useCalibrationsDueViewModel } from "../../../viewmodels/operation/reports/useCalibrationsDueViewModel";


// ============================================================
// CALIBRATION REPORT
// ============================================================

const CalibrationReport = () => {

  // ============================================================
  // CUSTOMER
  // ============================================================

  const [selectedCustomer, setSelectedCustomer] =
    useState<any>(null);

  const [customerSearchText, setCustomerSearchText] =
    useState("");

  const { loadCustomerOptions } =
    useCustomerViewModel();


  // ============================================================
  // REPORT VIEW MODEL
  // ============================================================

  const {
    calibrationCertificates,
    loading,

    startDate,
    setStartDate,

    endDate,
    setEndDate,

    searchReport,
    clearFilters,
  } = useCalibrationsDueViewModel();


  // ============================================================
  // SEARCH
  // ============================================================

  const handleSearch = async () => {

    await searchReport(
      selectedCustomer?.value || ""
    );
  };


  // ============================================================
  // CLEAR
  // ============================================================

  const handleClear = async () => {

    setSelectedCustomer(null);

    setCustomerSearchText("");

    await clearFilters();
  };


  // ============================================================
  // FORMAT DATE
  // ============================================================

  const formatDate = (date: any) => {

    if (!date) {
      return "-";
    }

    return new Date(date).toLocaleDateString(
      "en-GB"
    );
  };


  // ============================================================
  // FORMAT MULTI-LINE VALUES
  // ============================================================

  const formatMultiLine = (
    value: string | null | undefined
  ) => {

    if (!value) {
      return "-";
    }

    return value
      .split(/\r?\n/)
      .map((line, index) => (
        <div key={index}>
          {line}
        </div>
      ));
  };


  // ============================================================
  // VIEW CALIBRATION CERTIFICATE
  // ============================================================

  const handleView = (id: string) => {

    if (!id) {
      return;
    }

    window.open(
      `/calibration-certificates/view/${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <DashboardLayout>

      <div className="p-4 md:p-6">

        {/* ======================================================
            HEADER
        ====================================================== */}

        <div className="mb-6">

          <h1 className="text-2xl font-bold text-slate-800">
            Calibration Due Report
          </h1>

          <p className="text-slate-500 mt-1">
            View customers with calibration certificates due
          </p>

        </div>


        {/* ======================================================
            FILTERS
        ====================================================== */}

        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

            {/* ==================================================
                CUSTOMER
            ================================================== */}

            <div className="lg:col-span-2">

              <label className="block text-sm font-medium text-slate-700 mb-2">
                Customer
              </label>

              <div className="relative">

                <AsyncPaginate
                  value={selectedCustomer}

                  loadOptions={loadCustomerOptions}

                  additional={{
                    page: 1,
                  }}

                  placeholder="Search Customer"

                  inputValue={customerSearchText}

                  onInputChange={(value) => {

                    setCustomerSearchText(value);

                    return value;
                  }}

                  onChange={(selected: any) => {

                    setSelectedCustomer(selected);
                  }}
                />


                {/* CLEAR CUSTOMER */}

                {selectedCustomer && (

                  <button
                    type="button"

                    onClick={() => {

                      setSelectedCustomer(null);

                      setCustomerSearchText("");
                    }}

                    className="
                      absolute
                      right-10
                      top-1/2
                      -translate-y-1/2
                      z-10
                      w-6
                      h-6
                      rounded-full
                      bg-red-100
                      text-red-600
                      hover:bg-red-200
                      flex
                      items-center
                      justify-center
                      text-sm
                      font-bold
                    "
                  >
                    ×
                  </button>

                )}

              </div>

            </div>


            {/* ==================================================
                START DATE
            ================================================== */}

            <div>

              <label className="block text-sm font-medium text-slate-700 mb-2">
                Start Date
              </label>

              <input
                type="date"

                value={startDate}

                onChange={(e) => {
                  setStartDate(e.target.value);
                }}

                className="
                  w-full
                  h-[42px]
                  px-3
                  rounded-xl
                  border
                  border-slate-300
                  focus:outline-none
                  focus:ring-2
                  focus:ring-primary
                "
              />

            </div>


            {/* ==================================================
                END DATE
            ================================================== */}

            <div>

              <label className="block text-sm font-medium text-slate-700 mb-2">
                End Date
              </label>

              <input
                type="date"

                value={endDate}

                onChange={(e) => {
                  setEndDate(e.target.value);
                }}

                className="
                  w-full
                  h-[42px]
                  px-3
                  rounded-xl
                  border
                  border-slate-300
                  focus:outline-none
                  focus:ring-2
                  focus:ring-primary
                "
              />

            </div>

          </div>


          {/* ====================================================
              BUTTONS
          ==================================================== */}

          <div className="flex items-center gap-3 mt-5">

            <button
              type="button"

              onClick={handleSearch}

              disabled={loading}

              className="
                h-[42px]
                px-6
                rounded-xl
                bg-primary
                text-white
                font-medium
                hover:opacity-90
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
            >
              {loading ? "Loading..." : "Search"}
            </button>


            <button
              type="button"

              onClick={handleClear}

              disabled={loading}

              className="
                h-[42px]
                px-6
                rounded-xl
                border
                border-slate-300
                text-slate-700
                font-medium
                hover:bg-slate-50
                disabled:opacity-50
              "
            >
              Clear
            </button>

          </div>


          {/* ====================================================
              SELECTED CUSTOMER
          ==================================================== */}

          {selectedCustomer && (

            <div className="mt-4 text-sm text-slate-500">

              Selected Customer:

              <span className="font-semibold ml-1 text-slate-700">
                {selectedCustomer.label}
              </span>

            </div>

          )}

        </div>


        {/* ======================================================
            CALIBRATION REPORT TABLE
        ====================================================== */}

        <div
          className="
            bg-white
            rounded-2xl
            shadow-sm
            border
            border-slate-200
            overflow-hidden
          "
        >

          {/* ====================================================
              TABLE HEADER
          ==================================================== */}

          <div className="px-5 py-4 border-b border-slate-200">

            <h2 className="text-lg font-semibold text-slate-800">
              Calibration Due
            </h2>

          </div>


          {/* ====================================================
              LOADING
          ==================================================== */}

          {loading ? (

            <div className="flex justify-center items-center py-16">

              <div
                className="
                  w-8
                  h-8
                  border-4
                  border-slate-200
                  border-t-primary
                  rounded-full
                  animate-spin
                "
              />

            </div>

          ) : calibrationCertificates.length === 0 ? (

            <div className="text-center py-16 text-slate-500">
              No calibration certificates found.
            </div>

          ) : (

            /* ==================================================
               TABLE
            ================================================== */

            <div className="overflow-x-auto">

              <table className="w-full min-w-[1000px]">

                <thead className="bg-slate-100">

                  <tr>

                    {/* CUSTOMER NAME */}

                    <th className="border-b p-3 text-left text-sm font-semibold">
                      Customer Name
                    </th>


                    {/* PHONE */}

                    <th className="border-b p-3 text-left text-sm font-semibold">
                      Phone
                    </th>


                    {/* EMAIL */}

                    <th className="border-b p-3 text-left text-sm font-semibold">
                      Email
                    </th>


                    {/* ADDRESS */}

                    <th className="border-b p-3 text-left text-sm font-semibold">
                      Address
                    </th>


                    {/* DUE DATE */}

                    <th className="border-b p-3 text-left text-sm font-semibold">
                      Due Date
                    </th>


                    {/* VIEW */}

                    <th className="border-b p-3 text-center text-sm font-semibold">
                      Action
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {calibrationCertificates.map(
                    (item: any, index: number) => {

                      const id =
                        item.id ||
                        item.Id;

                      return (

                        <tr
                          key={id || index}
                          className="hover:bg-slate-50 align-top"
                        >

                          {/* CUSTOMER NAME */}

                          <td
                            className="
                              border-b
                              p-3
                              text-sm
                              font-medium
                              text-slate-800
                            "
                          >
                            {item.customerName ?? "-"}
                          </td>


                          {/* PHONE */}

                          <td
                            className="
                              border-b
                              p-3
                              text-sm
                              text-slate-600
                              whitespace-nowrap
                            "
                          >
                            {formatMultiLine(
                              item.customerPhone
                            )}
                          </td>


                          {/* EMAIL */}

                          <td
                            className="
                              border-b
                              p-3
                              text-sm
                              text-slate-600
                            "
                          >
                            {item.customerEmail ?? "-"}
                          </td>


                          {/* ADDRESS */}

                          <td
                            className="
                              border-b
                              p-3
                              text-sm
                              text-slate-600
                              min-w-[250px]
                            "
                          >
                            {formatMultiLine(
                              item.customerAddress
                            )}
                          </td>


                          {/* DUE DATE */}

                          <td
                            className="
                              border-b
                              p-3
                              text-sm
                              font-medium
                              text-slate-700
                              whitespace-nowrap
                            "
                          >
                            {formatDate(
                              item.dueDate
                            )}
                          </td>


                          {/* VIEW */}

                          <td
                            className="
                              border-b
                              p-3
                              text-sm
                              text-center
                            "
                          >

                            <button
                              type="button"

                              onClick={() =>
                                handleView(id)
                              }

                              className="
                                bg-blue-500
                                hover:bg-blue-600
                                text-white
                                px-4
                                py-2
                                rounded-lg
                                transition
                              "
                            >
                              View
                            </button>

                          </td>

                        </tr>

                      );
                    }
                  )}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </div>

    </DashboardLayout>
  );
};

export default CalibrationReport;